import {useCharacter} from '../character-context';
import {ABILITIES} from '../model/abilities';
import {unassignedAbilities} from '../model/ability-generation';
import {alignmentLabel} from '../model/alignments';
import {choicesWithErrors, incompleteChoices} from '../model/choice-limits';
import type {FeatureInstance} from '../model/feature-tree';
import type {StartingSource} from '../model/starting-equipment';

export type MasterStep = 'class' | 'origin' | 'specie' | 'abilities' | 'equipment';

export type StepSummary = {
	step: MasterStep;
	title: string;
	/** что выбрано на шаге; пусто — шаг ещё не начат */
	value?: string;
	/** сколько выборов на шаге не сделано */
	missingCount: number;
	/** сколько выборов на шаге с ошибкой: повышение характеристики больше предела */
	errorCount: number;
};

export function useStepSummaries(): StepSummary[] {
	const {catalog, state, features, character} = useCharacter();
	const missingIn = (instances: FeatureInstance[]) => incompleteChoices(instances, catalog, character).length;
	const errorsIn = (instances: FeatureInstance[]) => choicesWithErrors(instances, character).length;
	const origin = state.originId ? catalog.getOrigin(state.originId) : undefined;
	const startingClass = state.classes[0] ? catalog.getClass(state.classes[0].classId) : undefined;
	const equipmentSources: {source: StartingSource; label: string; text?: string}[] = [
		{source: 'class', label: 'класс', text: startingClass?.equipment},
		{source: 'origin', label: 'предыстория', text: origin?.equipment},
	];

	return [
		{
			step: 'class',
			title: 'Класс',
			value: features.classes
				.map(({classData, classLevel}) => `${classData.title} ${classLevel.level}`)
				.join(' + '),
			missingCount: missingIn(features.classes.flatMap((classFeatures) => classFeatures.features)),
			errorCount: errorsIn(features.classes.flatMap((classFeatures) => classFeatures.features)),
		},
		{
			step: 'origin',
			title: 'Происхождение',
			value: [origin?.title, state.alignment && alignmentLabel(state.alignment)].filter(Boolean).join(', '),
			missingCount: missingIn(features.origin) + (origin && !state.alignment ? 1 : 0),
			errorCount: errorsIn(features.origin),
		},
		{
			step: 'specie',
			title: 'Вид',
			value: state.specieId ? catalog.getSpecie(state.specieId)?.title : undefined,
			missingCount: missingIn(features.specie),
			errorCount: errorsIn(features.specie),
		},
		{
			step: 'abilities',
			title: 'Характеристики',
			value: ABILITIES.map((ability) => state.baseAbilities[ability]).join(' · '),
			missingCount: unassignedAbilities(state.abilityGeneration),
			errorCount: character.abilityOverflows.length,
		},
		{
			step: 'equipment',
			title: 'Снаряжение',
			value: equipmentSources
				.filter(({source}) => state.startingEquipment?.[source])
				.map(({source, label}) => `${label}: ${state.startingEquipment![source]!.option}`)
				.join(', '),
			missingCount: equipmentSources.filter(({source, text}) => text && !state.startingEquipment?.[source])
				.length,
			errorCount: 0,
		},
	];
}

/** создание закончено: на всех шагах всё выбрано и нет ошибок, лист можно открыть; снаряжение может быть пустым */
export const isCreationComplete = (steps: StepSummary[]) =>
	steps.every(
		({step, value, missingCount, errorCount}) => (step === 'equipment' || !!value) && !missingCount && !errorCount,
	);
